'use client';

import ScrollHelperStandart from '@lib/tools/helpers/ScrollHelperStandart';
import { useViewport } from '@lib/tools/hooks/useViewport';
import {
  getScrollSmoother,
  isNewsRoute,
  preserveNewsScroll,
  saveNewsScrollPosition,
} from '@lib/tools/scroll/scrollPosition';
import { usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

const NewsUIContext = createContext(null);

export const useNewsUI = () => {
  const ctx = useContext(NewsUIContext);
  if (!ctx) {
    throw new Error('useNewsUI must be used inside NewsUIProvider');
  }
  return ctx;
};

const schedulePreserveNewsScroll = () => {
  preserveNewsScroll();
  requestAnimationFrame(() => {
    preserveNewsScroll();
    requestAnimationFrame(preserveNewsScroll);
  });
  setTimeout(preserveNewsScroll, 0);
  setTimeout(preserveNewsScroll, 50);
  setTimeout(preserveNewsScroll, 150);
};

export default function NewsUIProvider({ children }) {
  const { isMobile } = useViewport();
  const [showAll, setShowAll] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    const save = () => saveNewsScrollPosition();
    window.addEventListener('scroll', save, { passive: true });

    let smootherScroller = null;
    let intervalId = 0;

    const attachSmootherListener = () => {
      const scroller = getScrollSmoother()?.scrollTrigger?.scroller;
      if (!scroller || scroller === smootherScroller) return Boolean(scroller);

      if (smootherScroller) {
        smootherScroller.removeEventListener('scroll', save);
      }

      smootherScroller = scroller;
      smootherScroller.addEventListener('scroll', save, { passive: true });
      return true;
    };

    if (!attachSmootherListener()) {
      intervalId = window.setInterval(() => {
        if (attachSmootherListener()) {
          window.clearInterval(intervalId);
        }
      }, 100);
    }

    return () => {
      window.removeEventListener('scroll', save);
      smootherScroller?.removeEventListener('scroll', save);
      window.clearInterval(intervalId);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isNewsRoute(pathname)) return;
    schedulePreserveNewsScroll();
  }, [pathname]);

  useEffect(() => {
    if (!isNewsRoute(pathname)) return;
    schedulePreserveNewsScroll();
  }, [pathname]);

  useEffect(() => {
    if (isMobile) {
      setShowAll(false);
    }
  }, [isMobile]);

  const contextValue = useMemo(
    () => ({
      showAll,
      setShowAll,
      isMobile,
      saveScrollPosition: saveNewsScrollPosition,
    }),
    [showAll, isMobile],
  );

  return (
    <NewsUIContext.Provider value={contextValue}>
      {children}
      <ScrollHelperStandart />
    </NewsUIContext.Provider>
  );
}
